import React from 'react';
import { ACTION_LABELS, STATUS_LABELS, RESOURCE_TYPE_LABELS } from './constants';
import {
  TableWrapper,
  Table,
  Thead,
  Th,
  Tbody,
  Tr,
  Td,
  StatusChip,
  Dot,
  ActionBadge,
  ResourceType,
  UserCell,
  UserName,
  UserEmail,
  Pagination,
  PageButtons,
  PageBtn,
  EmptyRow,
  TableScrollBody,
  TableSpinner as Spinner
} from './style';

const formatTimestamp = iso => {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const relativeTime = isoStr => {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return 'a few seconds ago';
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
};

const ActivityLogTable = ({
  data,
  isLoading,
  page,
  totalPages,
  total,
  size,
  onPageChange,
  onRowClick,
  selectedId,
  highlightId,
  projectsById = {},
  periodsById = {}
}) => {
  const items = data?.items ?? [];

  return (
    <TableWrapper>
      <TableScrollBody>
        <Table>
          <Thead>
            <tr>
              <Th>Operation name</Th>
              <Th>Project</Th>
              <Th>Period</Th>
              <Th>Resource type</Th>
              <Th>Resource name</Th>
              <Th>Status</Th>
              <Th>Time</Th>
              <Th>Timestamp</Th>
              <Th>Initiated by</Th>
              <Th>Duration</Th>
            </tr>
          </Thead>
          <Tbody>
            {isLoading ? (
              <EmptyRow>
                <Td colSpan={10}>
                  <Spinner />
                </Td>
              </EmptyRow>
            ) : items.length === 0 ? (
              <EmptyRow>
                <Td colSpan={10}>No audit log entries found.</Td>
              </EmptyRow>
            ) : (
              items.map(item => {
                const projectId = item.metadata?.context?.project_id;
                const periodId = item.metadata?.context?.period_id;
                const projectName = projectId
                  ? (projectsById[projectId] ?? item.metadata?.context?.project_name ?? null)
                  : (item.metadata?.context?.project_name ?? null);
                const periodObj = periodId ? (periodsById[periodId] ?? null) : null;
                const periodLabel = periodObj
                  ? `${periodObj.period} ${periodObj.year}`
                  : (item.metadata?.context?.period_name ?? null);
                return (
                  <Tr
                    key={item.id}
                    onClick={() => onRowClick(item)}
                    $highlighted={item.id === highlightId || item.id === selectedId}
                  >
                    <Td>
                      <ActionBadge $action={item.action}>
                        {ACTION_LABELS[item.action] ?? item.action}
                      </ActionBadge>
                      {item.action === 'UPDATE' &&
                        item.metadata?.diff &&
                        (() => {
                          const count = Object.keys(item.metadata.diff.from || {}).length;
                          return count > 0 ? (
                            <span
                              style={{
                                fontSize: 10,
                                background: '#eff6ff',
                                color: '#2563eb',
                                borderRadius: 10,
                                padding: '1px 6px',
                                marginLeft: 6,
                                fontWeight: 600,
                                verticalAlign: 'middle',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {count} field{count !== 1 ? 's' : ''} changed
                            </span>
                          ) : null;
                        })()}
                    </Td>
                    <Td
                      style={{
                        fontSize: 12,
                        color: '#111827',
                        maxWidth: 160,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {projectName ?? <span style={{ color: '#d1d5db' }}>—</span>}
                    </Td>
                    <Td style={{ fontSize: 12, color: '#111827', whiteSpace: 'nowrap' }}>
                      {periodLabel ?? <span style={{ color: '#d1d5db' }}>—</span>}
                    </Td>
                    <Td>
                      {item.resource_type ? (
                        <ResourceType style={{ fontSize: 12 }}>
                          {RESOURCE_TYPE_LABELS[item.resource_type] ?? item.resource_type}
                        </ResourceType>
                      ) : (
                        <span style={{ color: '#d1d5db' }}>—</span>
                      )}
                    </Td>
                    <Td style={{ fontSize: 12, color: '#111827' }}>
                      {item.resource_name ?? <span style={{ color: '#d1d5db' }}>—</span>}
                    </Td>
                    <Td>
                      <StatusChip $status={item.status}>
                        <Dot $status={item.status} />
                        {STATUS_LABELS[item.status] ?? item.status}
                      </StatusChip>
                    </Td>
                    <Td style={{ color: '#6b7280', whiteSpace: 'nowrap' }}>
                      {relativeTime(item.timestamp)}
                    </Td>
                    <Td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>
                      {formatTimestamp(item.timestamp)}
                    </Td>
                    <Td>
                      {item.user_name ? (
                        <UserCell>
                          <UserName>{item.user_name}</UserName>
                          {item.user_email && <UserEmail>{item.user_email}</UserEmail>}
                        </UserCell>
                      ) : (
                        <span style={{ color: '#9ca3af' }}>System</span>
                      )}
                    </Td>
                    <Td style={{ color: '#6b7280', fontSize: 12 }}>
                      {item.duration_ms != null ? `${item.duration_ms}ms` : '—'}
                    </Td>
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </TableScrollBody>

      {!isLoading && items.length > 0 && (
        <Pagination>
          <span>
            {(() => {
              const from = total === 0 ? 0 : (page - 1) * size + 1;
              const to = Math.min(page * size, total);
              return `${from}–${to} of ${total} ${total === 1 ? 'item' : 'items'}`;
            })()}{' '}
            · Page {page} of {totalPages}
          </span>
          <PageButtons>
            <PageBtn disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
              ← Prev
            </PageBtn>
            <PageBtn disabled={page >= totalPages} onClick={() => onPageChange(page + 1)}>
              Next →
            </PageBtn>
          </PageButtons>
        </Pagination>
      )}
    </TableWrapper>
  );
};

export default ActivityLogTable;
